import React from 'react';
import { Check, Clock, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDate } from '../lib/format';
import type { Approval, ApprovalLevel } from '../types';

const LEVEL_LABEL: Record<ApprovalLevel, string> = {
  hr: 'HR',
  finance: 'Finance',
  group: 'Group Finance',
  final: 'Final sign-off',
};

/** The name a stage is shown under: the policy's own label, else the canonical one. */
export const approvalLevelLabel = (a: Pick<Approval, 'level' | 'label'>) =>
  a.label?.trim() || LEVEL_LABEL[a.level] || a.level;

interface ApprovalTrackerProps {
  approvals: Approval[];
}

/** The run's approval chain (HR → Finance → [Group Finance] → Final, as its policy
 *  snapshot defines it), rendered as a connected stepper. A Group Finance-scoped stage
 *  carries a badge: only someone with access to every company can sign it. */
export const ApprovalTracker: React.FC<ApprovalTrackerProps> = ({ approvals }) => (
  <ol className="space-y-0">
    {approvals.map((a, i) => {
      const isLast = i === approvals.length - 1;
      const approved = a.decision === 'approved';
      const rejected = a.decision === 'rejected';
      return (
        <li key={a.level} className="flex gap-3">
          <div className="flex flex-col items-center">
            <span
              className={cn(
                'flex h-8 w-8 items-center justify-center rounded-full border-2',
                approved && 'border-[#166534] bg-[#DCFCE7] text-[#166534]',
                rejected && 'border-[#991B1B] bg-[#FEE2E2] text-[#991B1B]',
                !approved && !rejected && 'border-border bg-card text-muted-foreground',
              )}
            >
              {approved ? <Check className="h-4 w-4" /> : rejected ? <X className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
            </span>
            {!isLast && <span className={cn('w-0.5 flex-1 min-h-[1.25rem]', approved ? 'bg-[#166534]/40' : 'bg-border')} />}
          </div>
          <div className={cn('pb-5', isLast && 'pb-0')}>
            <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-charcoal">
              {approvalLevelLabel(a)}
              {a.scope === 'group' && (
                <span
                  className="rounded bg-info-soft px-1.5 py-0.5 text-[10px] font-medium text-info-strong"
                  title="Only an approver with access to every company can sign this stage"
                >
                  Group Finance
                </span>
              )}
            </p>
            {approved ? (
              <p className="text-xs text-muted-foreground">
                {a.actorName ? `Approved by ${a.actorName}` : 'Approved'}
                {a.decidedAt && ` · ${formatDate(a.decidedAt)}`}
              </p>
            ) : rejected ? (
              <p className="text-xs text-[#991B1B]">Rejected{a.note ? ` — ${a.note}` : ''}</p>
            ) : (
              <p className="text-xs text-muted-foreground">Awaiting decision</p>
            )}
          </div>
        </li>
      );
    })}
  </ol>
);
